import type { ReactNode } from 'react';
import { EmptyValue } from './EmptyValue.js';
import { Numeric } from './Numeric.js';
import styles from './AmountWithAux.module.css';

/** Narrow no-break space — French thousands grouping, stays with the digits. */
const GROUP_SEP = '\u202f';

/** Group integer digits; keep `.` decimal (admin money convention). */
export function formatAmountGrouped(value: number, decimals = 2): string {
  const fixed = value.toFixed(decimals);
  const neg = fixed.startsWith('-');
  const body = neg ? fixed.slice(1) : fixed;
  const [intPart, frac] = body.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEP);
  const main = frac != null ? `${grouped}.${frac}` : grouped;
  return neg ? `-${main}` : main;
}

export type AmountWithAuxProps = {
  value: unknown;
  /** Default Dhs (display); pass "" to omit when using `auxiliary` only (e.g. %). */
  currency?: string;
  /**
   * Shown muted after amount (e.g. HT / TTC). Prefer column/field labels for tax basis
   * so rows stay `12 345.00 Dhs` without repeating · TTC.
   */
  pricingMode?: 'HT' | 'TTC';
  /**
   * Full auxiliary tail (muted). Use for `%` or when `currency`/`pricingMode` don’t apply.
   * If set, overrides the built `currency · pricingMode` string.
   */
  auxiliary?: string;
  empty?: ReactNode;
  /** When true, finite 0 (and near-zero) uses `empty` instead of `0.00`. */
  zeroAsEmpty?: boolean;
  decimals?: number;
};

/** Tabular amount + muted auxiliary (currency, HT/TTC, %, …). Primary figure in mono; tail in body muted. */
export function AmountWithAux({
  value,
  currency = 'Dhs',
  pricingMode,
  auxiliary,
  empty,
  zeroAsEmpty = false,
  decimals = 2,
}: AmountWithAuxProps) {
  const emptyNode = empty ?? <EmptyValue kind="zero" />;
  if (value == null || value === '') {
    return <span className={styles.empty}>{emptyNode}</span>;
  }
  const n = typeof value === 'number' ? value : Number(value);
  if (zeroAsEmpty && Number.isFinite(n) && Math.abs(n) < 1e-9) {
    return <span className={styles.empty}>{emptyNode}</span>;
  }
  const main = Number.isFinite(n) ? formatAmountGrouped(n, decimals) : String(value);
  const aux =
    auxiliary !== undefined
      ? auxiliary.startsWith(' ')
        ? auxiliary
        : ` ${auxiliary}`
      : currency?.trim()
        ? ` ${currency.trim()}${pricingMode ? ` · ${pricingMode}` : ''}`
        : pricingMode
          ? ` ${pricingMode}`
          : undefined;

  return (
    <span className={styles.wrap}>
      <Numeric>{main}</Numeric>
      {aux ? <span className={styles.aux}>{aux}</span> : null}
    </span>
  );
}
